import { ChevronDown, Plus, X } from 'lucide-react'
import { useState } from 'react'
import { cn } from '../../utils/cn'
import ProductTabs from './ProductTabs'

// Renderiza texto com trechos em negrito marcados por **...** (peso 700 no
// Figma), preservando as quebras de linha via whitespace-pre-line no pai.
function renderRich(text) {
  return text.split('**').map((part, index) =>
    index % 2 === 1 ? (
      <strong key={index} className="font-inter-bold">
        {part}
      </strong>
    ) : (
      part
    ),
  )
}

// Renderiza texto rico: parágrafos separados por linha em branco (\n\n) com gap
// de 26px, quebras simples (\n) dentro do grupo, listas com marcador (linhas
// iniciadas por "- "), listas numeradas (2+ linhas consecutivas "N. ") e
// negrito (**). `className` define cor/fonte do bloco.
function RichBlocks({ text, className, quoteBorderClass }) {
  const groups = text.split('\n\n')

  return (
    <div className={cn('flex flex-col gap-[26px]', className)}>
      {groups.map((group, groupIndex) => {
        const lines = group.split('\n')
        const blocks = []
        let bullets = []
        let ordered = []
        const flushBullets = () => {
          if (bullets.length) {
            blocks.push({ type: 'ul', items: bullets })
            bullets = []
          }
        }
        const flushOrdered = () => {
          if (ordered.length >= 2) {
            blocks.push({
              type: 'ol',
              items: ordered.map(line => line.replace(/^\d+\.\s*/, '')),
            })
          } else if (ordered.length === 1) {
            // Número solto = texto numerado literal (não vira lista).
            blocks.push({ type: 'p', text: ordered[0] })
          }
          ordered = []
        }
        for (const line of lines) {
          if (line.startsWith('- ')) {
            flushOrdered()
            bullets.push(line.slice(2))
          } else if (line.startsWith('> ')) {
            flushBullets()
            flushOrdered()
            blocks.push({ type: 'quote', text: line.slice(2) })
          } else if (/^\d+\.\s/.test(line)) {
            flushBullets()
            ordered.push(line)
          } else {
            flushBullets()
            flushOrdered()
            blocks.push({ type: 'p', text: line })
          }
        }
        flushBullets()
        flushOrdered()

        return (
          <div key={groupIndex} className="flex flex-col">
            {blocks.map((block, blockIndex) =>
              block.type === 'ul' ? (
                <ul key={blockIndex} className="ms-6 list-disc">
                  {block.items.map((listItem, itemIndex) => (
                    <li key={itemIndex} className="leading-[26px]">
                      {renderRich(listItem)}
                    </li>
                  ))}
                </ul>
              ) : block.type === 'ol' ? (
                <ol key={blockIndex} className="ms-6 list-decimal">
                  {block.items.map((listItem, itemIndex) => (
                    <li key={itemIndex} className="leading-[26px]">
                      {renderRich(listItem)}
                    </li>
                  ))}
                </ol>
              ) : block.type === 'quote' ? (
                <div
                  key={blockIndex}
                  className={cn(
                    'border-l-4 pl-6',
                    quoteBorderClass ?? 'border-[#FF4D1C]',
                  )}
                >
                  <p className="italic leading-[26px]">
                    {renderRich(block.text)}
                  </p>
                </div>
              ) : (
                <p key={blockIndex} className="leading-[26px]">
                  {renderRich(block.text)}
                </p>
              ),
            )}
          </div>
        )
      })}
    </div>
  )
}

function FaqAnswer({ text, quoteBorderClass }) {
  return (
    <RichBlocks
      text={text}
      quoteBorderClass={quoteBorderClass}
      className="font-inter-regular text-base text-[#4A5565]"
    />
  )
}

// FAQ interno de um produto: lista de perguntas expansíveis (1ª aberta).
// Quando aberta, a pergunta fica vermelha (conforme o Figma).
function ProductFaqList({
  items = [],
  questionClass = 'text-[#454545]',
  openClass = 'text-[#CA3500]',
  iconClass = 'text-[#FF4D1C]',
  quoteBorderClass,
}) {
  const [openIndex, setOpenIndex] = useState(0)

  return (
    <ul className="flex flex-col">
      {items.map((item, index) => {
        const isOpen = openIndex === index
        const isLast = index === items.length - 1

        return (
          <li
            key={index}
            className={cn(
              'flex flex-col gap-3 px-6 pt-8 pb-6',
              !isLast && 'border-[#E5E7EB] border-b',
            )}
          >
            <button
              type="button"
              aria-expanded={isOpen}
              onClick={() =>
                setOpenIndex(current => (current === index ? -1 : index))
              }
              className="flex w-full items-start justify-between gap-6 text-left"
            >
              <span
                className={cn(
                  'font-inter-semibold text-base',
                  isOpen ? openClass : questionClass,
                )}
              >
                {item.question}
              </span>
              {isOpen ? (
                <X size={20} className={cn('shrink-0', iconClass)} />
              ) : (
                <Plus size={20} className={cn('shrink-0', iconClass)} />
              )}
            </button>

            {isOpen && item.answer && (
              <FaqAnswer
                text={item.answer}
                quoteBorderClass={quoteBorderClass}
              />
            )}
          </li>
        )
      })}
    </ul>
  )
}

// Conteúdo em colunas (ex.: aba Contratação): cada coluna tem um título e uma
// lista de itens com seta → verde, em cartões #F9FAFB.
function ProductColumns({ columns = [] }) {
  return (
    <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
      {columns.map((column, index) => (
        <div
          key={index}
          className="flex flex-col gap-4 rounded-[10px] bg-[#F9FAFB] p-8"
        >
          <h4 className="font-inter-bold text-base text-[#454545]">
            {column.title}
          </h4>
          <ul className="flex flex-col gap-4">
            {column.items.map((listItem, itemIndex) => (
              <li key={itemIndex} className="flex items-start">
                <span className="w-4 shrink-0 font-inter-regular text-xs leading-[22px] text-[#00D4B4]">
                  →
                </span>
                <span className="font-inter-regular text-sm leading-[22px] text-[#4A5565]">
                  {listItem}
                </span>
              </li>
            ))}
          </ul>
        </div>
      ))}
    </div>
  )
}

// Conteúdo "Para Quem": seções com título em negrito (#454545) e lista com
// marcador disc (#4A5565), separadas por uma linha (26px).
function ProductParaQuem({
  sections = [],
  titleClass = 'text-[#454545]',
  dividers = false,
}) {
  return (
    <div className="flex flex-col gap-[26px] rounded-[10px] border border-[#E5E7EB] bg-white px-6 pt-8 pb-6">
      {sections.map((section, index) => (
        <div
          key={index}
          className={cn(
            'flex flex-col',
            dividers && index > 0 && 'border-[#E5E7EB] border-t pt-[26px]',
          )}
        >
          <p
            className={cn(
              'font-inter-bold text-base leading-[26px]',
              titleClass,
            )}
          >
            {section.title}
          </p>
          {section.intro && (
            <p className="font-inter-regular text-base leading-[26px] text-[#4A5565]">
              {renderRich(section.intro)}
            </p>
          )}
          <ul className="ms-6 list-disc">
            {section.items.map((listItem, itemIndex) => (
              <li
                key={itemIndex}
                className="font-inter-regular text-base leading-[26px] text-[#4A5565]"
              >
                {renderRich(listItem)}
              </li>
            ))}
          </ul>
        </div>
      ))}
    </div>
  )
}

export default function ProductAccordion({
  intro = '',
  products = [],
  introHeading,
  badgeClass = 'border-[#FF4D1C]/20 bg-[#FF4D1C]/10 text-[#FF4D1C]',
  tabActiveClass,
  faqQuestionClass,
  faqOpenClass,
  faqIconClass,
  accentBorderClass,
  paraQuemTitleClass,
  paraQuemDividers = false,
}) {
  const [openId, setOpenId] = useState(products[0]?.id ?? null)
  const [activeTabByProduct, setActiveTabByProduct] = useState(() =>
    products.reduce((acc, product) => {
      acc[product.id] = product.tabs?.[0]?.id
      return acc
    }, {}),
  )

  const introParagraphs = Array.isArray(intro) ? intro : [intro]

  const toggle = id => setOpenId(current => (current === id ? null : id))

  const setActiveTab = (productId, tabId) =>
    setActiveTabByProduct(current => ({ ...current, [productId]: tabId }))

  return (
    <div className="w-full px-20">
      {introHeading && (
        <h2 className="font-inter-extrabold text-[28px] text-[#454545] pb-4">
          {introHeading}
        </h2>
      )}

      {introParagraphs.some(Boolean) && (
        <div className="flex flex-col gap-2 pb-6">
          {introParagraphs.filter(Boolean).map((paragraph, index) => (
            <p
              key={index}
              className="font-inter-regular text-base leading-relaxed text-[#454545]"
            >
              {paragraph}
            </p>
          ))}
        </div>
      )}

      <div className="flex flex-col gap-6">
        {products.map(product => {
          const isOpen = openId === product.id
          const activeTab =
            activeTabByProduct[product.id] ?? product.tabs?.[0]?.id
          const content = product.contentByTab?.[activeTab]
          const body = Array.isArray(content?.body)
            ? content.body
            : content?.body
              ? [content.body]
              : []

          return (
            <div
              key={product.id}
              className="overflow-hidden rounded-[10px] border border-[#D9D9D9] bg-white"
            >
              <button
                type="button"
                aria-expanded={isOpen}
                onClick={() => toggle(product.id)}
                className="flex w-full items-center justify-between gap-6 px-12 py-8 text-left"
              >
                <span className="flex items-center gap-6">
                  <span
                    className={cn(
                      'flex h-[38px] w-[61px] items-center justify-center rounded-[4px] border font-jetbrains-mono text-[14px] uppercase tracking-[1.4px]',
                      badgeClass,
                    )}
                  >
                    {product.badge}
                  </span>
                  <span className="font-inter-bold text-2xl text-[#454545]">
                    {product.title}
                  </span>
                </span>
                <ChevronDown
                  size={24}
                  className={cn(
                    'shrink-0 text-[#99A1AF] transition-transform duration-200',
                    isOpen && 'rotate-180',
                  )}
                />
              </button>

              {isOpen && (
                <div className="flex flex-col gap-10 px-12 pb-8">
                  <ProductTabs
                    tabs={product.tabs}
                    active={activeTab}
                    onChange={tabId => setActiveTab(product.id, tabId)}
                    {...(tabActiveClass ? { activeClass: tabActiveClass } : {})}
                  />

                  {content &&
                    (content.faq ? (
                      <div
                        role="tabpanel"
                        className="rounded-[10px] border border-[#E5E7EB] bg-white"
                      >
                        <ProductFaqList
                          items={content.faq}
                          {...(faqQuestionClass
                            ? { questionClass: faqQuestionClass }
                            : {})}
                          {...(faqOpenClass ? { openClass: faqOpenClass } : {})}
                          {...(faqIconClass ? { iconClass: faqIconClass } : {})}
                          quoteBorderClass={accentBorderClass}
                        />
                      </div>
                    ) : content.columns ? (
                      <div role="tabpanel">
                        <ProductColumns columns={content.columns} />
                      </div>
                    ) : content.sections ? (
                      <div role="tabpanel">
                        <ProductParaQuem
                          sections={content.sections}
                          dividers={paraQuemDividers}
                          {...(paraQuemTitleClass
                            ? { titleClass: paraQuemTitleClass }
                            : {})}
                        />
                      </div>
                    ) : (
                      <div
                        role="tabpanel"
                        className="flex flex-col gap-6 rounded-[10px] bg-[#F9FAFB] p-6 pb-8"
                      >
                        <div className="flex flex-col gap-10">
                          {content.eyebrow && (
                            <span className="font-jetbrains-mono text-xs uppercase tracking-[0.05em] text-[#6A7282]">
                              {content.eyebrow}
                            </span>
                          )}
                          {content.headline && (
                            <h3 className="font-inter-bold text-2xl leading-[30px] text-[#454545]">
                              {content.headline}
                            </h3>
                          )}
                        </div>
                        {typeof content.body === 'string' ? (
                          <RichBlocks
                            text={content.body}
                            quoteBorderClass={accentBorderClass}
                            className="font-inter-regular text-base text-[#364153]"
                          />
                        ) : (
                          body.map((paragraph, index) => (
                            <p
                              key={index}
                              className="font-inter-regular text-base leading-[26px] text-[#364153]"
                            >
                              {paragraph}
                            </p>
                          ))
                        )}
                      </div>
                    ))}
                </div>
              )}
            </div>
          )
        })}
      </div>
    </div>
  )
}
